import { CmdTooltip } from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import * as ExportState from '../../Export/ExportState';
import LoaderService from '../../../Services/LoaderService';
import { IDownload } from '../../Export/ExportState';
import { ICmdTableFilter } from '../../CmdTable/types';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../../store/recoil/TableState';
import { BLE_USAGE_TABLE_NAME } from '../constants';
import { filterForRequestAtomFamily, sortFilterAtomFamily } from '../../CmdTable/CmdTableState';
import useAnalytics from '../../../hooks/useAnalytics';
import { getExport, getTableName } from '../../CmdTable/CmdTableService';

const EXPORT_URL = '/v2/report/104/export';

export default function Export(): JSX.Element {
  const { formatMessage: f } = useIntl();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(BLE_USAGE_TABLE_NAME)
  );
  const setIsActiveExport = useSetRecoilState<false | string>(ExportState.isActive);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(BLE_USAGE_TABLE_NAME)
  );
  const setDownloads = useSetRecoilState<IDownload[]>(ExportState.downloads);
  const [minDatepickerTimestamp, setMinDatepickerTimestamp] = React.useState<string>('');
  const [maxDatepickerTimestamp, setMaxDatepickerTimestamp] = React.useState<string>('');
  const datepickerSettings = useRecoilValue<IDatepickerSettings | null>(setTableDatepickerAtom);
  const { trackEvent } = useAnalytics();

  React.useEffect(() => {
    if (
      datepickerSettings &&
      datepickerSettings.timestamp &&
      datepickerSettings.timestamp.date &&
      datepickerSettings.timestamp.date.length === 2
    ) {
      if (
        dayjs(datepickerSettings.timestamp.date[0]).format('DD.MM.YYYY HH:mm') !==
        dayjs(minDatepickerTimestamp).format('DD.MM.YYYY HH:mm')
      ) {
        setMinDatepickerTimestamp(dayjs(datepickerSettings.timestamp.date[0]).toISOString());
      }
      if (
        dayjs(datepickerSettings.timestamp.date[1]).format('DD.MM.YYYY HH:mm') !==
        dayjs(maxDatepickerTimestamp).format('DD.MM.YYYY HH:mm')
      ) {
        setMaxDatepickerTimestamp(dayjs(datepickerSettings.timestamp.date[1]).toISOString());
      }
    }
  }, [maxDatepickerTimestamp, minDatepickerTimestamp, datepickerSettings]);

  const handleExport = async () => {
    trackEvent([`table ${getTableName(BLE_USAGE_TABLE_NAME)}`, 'click', 'Download usage bluecoin']);
    LoaderService.showLoader(true);
    let newFilterRequest = { ...filterForRequest };
    if (filterForRequest.name && typeof filterForRequest.name === 'object') {
      newFilterRequest = {
        ...filterForRequest,
        name: Object.values(filterForRequest.name),
      };
    }
    const data = {
      filter: {
        ...newFilterRequest,
        timestamp: {
          min: minDatepickerTimestamp,
          max: maxDatepickerTimestamp,
        },
      },
      sort: {
        ...sortFilter,
      },
    };
    const response = await getExport(EXPORT_URL, data);
    if (response) {
      setIsActiveExport(response.jobId);
      setDownloads((oldState: IDownload[]) => {
        const job = oldState.find((item: IDownload) => item.jobId === response.jobId);
        if (job) {
          const updatedJob: IDownload = {
            ...job,
            isDownloaded: false,
            code: 'BluecoinUsage',
            percentage: 0,
            generating_end: dayjs().toISOString(),
          };
          const newState = [
            ...oldState.filter((item: IDownload) => item.jobId !== response.jobId),
            updatedJob,
          ];
          return newState;
        }
        const newJob: IDownload = {
          jobId: response.jobId,
          isDownloaded: false,
          code: 'BluecoinUsage',
          percentage: 0,
          generating_start: dayjs().toISOString(),
          generating_end: null,
          filename: 'BluecoinUsage',
        };
        const newState = [...oldState, newJob];
        return newState;
      });
    }
    LoaderService.showLoader(false);
  };

  return (
    <div className="col-12 col-md-auto">
      <CmdTooltip
        content={f({ id: 'bluecoin.export.download.tooltip' })}
        customCss={{ whiteSpace: 'pre-wrap', top: '38px' }}
        width={150}
      >
        <a
          onClick={handleExport}
          href="#"
          className="e-button e-button--square px-2"
          data-cy="tlc-lite-export"
        >
          <i className="cmd-icons cmd-icon-download mr-2" />
          {f({ id: 'bluecoin.export.download' })}
        </a>
      </CmdTooltip>
    </div>
  );
}
